import { useMemo, useEffect } from 'react';
import useLookupsByType from './useLookupsByType';
import { useStoreActions } from '../../models/hooks';
import { Lookups } from '../../firebase/firestore/documents/lookups';

interface LookupMap {
  offerRejectedReason: Lookups[];
  offboardReason: Lookups[];
  offboardingType: Lookups[];
  dismissalReasons: Lookups[];
  dismissalReasonsAutomation: Lookups[];
  hireFormRejectionReasons: Lookups[];
  refusedAppointmentReasons: Lookups[];
  interviewCancelReasons: Lookups[];
}
interface UseLookupsResult {
  lookupsReady: boolean;
  lookups: LookupMap;
}

export const useLookups = (): UseLookupsResult => {
  const setLookupsByType = useStoreActions((a) => a.messaging.setLookupsByType);

  const offerRejectedReasons = useLookupsByType('offerRejectedReason');
  const offboardedReasons = useLookupsByType('offboardReason');
  const offboardedTypes = useLookupsByType('offboardingType');
  const dismissalReasons = useLookupsByType('dismissalReasons');
  const dismissalReasonAutomation = useLookupsByType('dismissalReasonsAutomation');
  const hireFormRejectionReasons = useLookupsByType('hireFormRejectionReasons');
  const interviewRefusedReasons = useLookupsByType('refusedAppointmentReasons');
  const interviewCancelReasons = useLookupsByType('interviewCancelReasons');

  // memoize lookupMap so reference doesn't change every render
  const lookupMap: LookupMap = useMemo(
    () => ({
      offerRejectedReason: offerRejectedReasons,
      offboardReason: offboardedReasons,
      offboardingType: offboardedTypes,
      dismissalReasons,
      dismissalReasonsAutomation: dismissalReasonAutomation,
      hireFormRejectionReasons,
      refusedAppointmentReasons: interviewRefusedReasons,
      interviewCancelReasons,
    }),
    [
      offerRejectedReasons,
      offboardedReasons,
      offboardedTypes,
      dismissalReasons,
      dismissalReasonAutomation,
      hireFormRejectionReasons,
      interviewRefusedReasons,
      interviewCancelReasons,
    ],
  );

  const lookupsReady = useMemo(() => (Object.values(lookupMap) as Lookups[][]).every((arr) => arr.length > 0), [lookupMap]);

  useEffect(() => {
    if (!lookupsReady) return;

    (Object.entries(lookupMap) as [string, Lookups[]][]).forEach(([type, data]) => {
      setLookupsByType({ type, data });
    });
  }, [lookupsReady, lookupMap, setLookupsByType]);

  return {
    lookupsReady,
    lookups: lookupMap,
  };
};
